import { BuilderPageComponent } from '.';
import { ValueOf } from './to-pandora-index';

export const HERO_BANNER_PAGE_COMPONENT_TYPES = {
  HERO_BANNER_BUILDER: 'hero-banner-builder',
  HERO_BANNER_CONTENTFUL: 'hero-banner-contentful',
} as const;

export const HERO_BANNER_BUILDER_STYLE_MODE = {
  LIGHT: 'Light',
  DARK: 'Dark',
} as const;

export const HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION = {
  IMAGE_BACKGROUND: 'Image Background',
  LEFT_RIGHT: 'Left/Right',
  UP_DOWN: 'Up/Down',
} as const;

export const HERO_BANNER_BUILDER_CTA_TYPE = {
  PRIMARY_BUTTON: 'Primary Button',
  ACTION_LINK: 'Action Link',
  TRANSPARENT_BUTTON: 'Transparent Button',
  VIDEO: 'Video',
  APPLE_STORE: 'Apple Store',
  GOOGLE_PLAY_STORE: 'Google Play Store',
} as const;

export const HERO_BANNER_CONTENTFUL_MODEL = {
  BLOCK_CONTENT_BLOCK: 'Block - Content Block',
  BLOCK_CONTENT_VIDEO: 'Block - Content Video',
  BLOCK_APP_DOWNLOAD_BLOCK: 'Block - App Download Block',
} as const;

// Type definitions
export type HeroBannerPageComponentType = ValueOf<typeof HERO_BANNER_PAGE_COMPONENT_TYPES>;
export type HeroBannerContentfulModel = ValueOf<typeof HERO_BANNER_CONTENTFUL_MODEL>;
type HeroBannerStyleMode = ValueOf<typeof HERO_BANNER_BUILDER_STYLE_MODE>;

// Interfaces
export interface HeroBannerPageComponentBase extends BuilderPageComponent {
  options: {
    entryId: string;
  };
}

export interface HeroBannerContentfulPageComponent extends HeroBannerPageComponentBase {
  id: typeof HERO_BANNER_PAGE_COMPONENT_TYPES.HERO_BANNER_CONTENTFUL;
  options: HeroBannerPageComponentBase['options'] & {
    contentfulModel?: HeroBannerContentfulModel;
  };
}

export interface HeroBannerBuilderPageComponent extends HeroBannerPageComponentBase {
  id: typeof HERO_BANNER_PAGE_COMPONENT_TYPES.HERO_BANNER_BUILDER;
}

export interface HeroBannerCTA {
  ctaType?: string;
  ctaLabel?: string;
  link?: string;
  altText?: string;
  newTab?: boolean;
}

export interface HeroBannerText {
  highlight?: string;
  ctas: HeroBannerCTA[];
  title?: string;
  description?: string;
  subDescription?: string;
  disclaimer?: string;
}

export interface HeroBannerImage {
  image?: string;
  imageFit?: string;
  aspectRatio?: {
    width?: number;
    height?: number;
  };
}

export interface HeroBannerImageSet {
  desktopImage?: HeroBannerImage;
  tabletImage?: HeroBannerImage;
  mobileImage?: HeroBannerImage;
  altText: string;
}

interface HeroBannerContent {
  text: HeroBannerText;
  image?: HeroBannerImageSet;
}

export type HeroBannerTextAlignment = 'left' | 'center' | 'right';

interface HeroBannerTextAlignmentDevices {
  desktop: HeroBannerTextAlignment;
  tablet: HeroBannerTextAlignment;
  mobile: HeroBannerTextAlignment;
}

interface HeroBannerLayoutDevices {
  desktop:
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.IMAGE_BACKGROUND
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.LEFT_RIGHT;
  tablet:
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.IMAGE_BACKGROUND
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.LEFT_RIGHT
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.UP_DOWN;
  mobile:
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.IMAGE_BACKGROUND
    | typeof HERO_BANNER_BUILDER_TEXT_LAYOUT_POSITION.UP_DOWN;
}

export interface HeroBannerStyle {
  textWidth: string;
  mode: HeroBannerStyleMode;
  textAlignment: HeroBannerTextAlignmentDevices;
  layout: HeroBannerLayoutDevices;
  fliporder?: boolean;
}

export interface BuilderHeroBanner {
  internalName: string;
  content: HeroBannerContent;
  style: HeroBannerStyle;
}

// Type guards
export function isHeroBannerPageComponentType(id: string): id is HeroBannerPageComponentType {
  return Object.values(HERO_BANNER_PAGE_COMPONENT_TYPES).some((value) => value === id);
}
